import { useAction } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { Avatar } from '@blade/ui-kit'
import { unsafeUrlDialogEvent } from '@root/entities/deeplinking/unsafe-url-dialog'
import { type TwitterSocialProfile } from '@root/entities/socials/model/socials'
import { normalizeXHref } from '@root/entities/socials/model/socials-normalize'
import { useFormatDuration } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import { ImagePopover } from '@root/shared/ui'
import { ExternalUrlIcon, SocialVerifiedIcon } from '@root/shared/ui-kit'

export type ExploreDevTwitterTooltipProps = {
  twitterInfo: TwitterSocialProfile
}

type HeadingNameProps = {
  name: string
  username: string
  verified?: boolean
}

export const HeadingName = ({ name, verified, username }: HeadingNameProps) => {
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const profileUrl = useMemo(() => normalizeXHref(username), [username])

  const handleOpenProfile = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    openUnsafeUrlDialog([profileUrl, 'x'])
  }

  return (
    <NameRoot href={profileUrl} onClick={handleOpenProfile}>
      {name}
      {verified ? <SocialVerifiedIcon size={14} color="#108CE1" /> : undefined}
    </NameRoot>
  )
}

type HeadingUsernameProps = {
  username: string
  createdAt?: string
}

export const HeadingUsername = ({
  username,
  createdAt,
}: HeadingUsernameProps) => {
  const { formatDuration } = useFormatDuration()
  const { t } = useTranslation()
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const profileUrl = useMemo(() => normalizeXHref(username), [username])

  const createdAtFormatted = useMemo(
    () =>
      createdAt
        ? formatDuration({
            startAt: new Date(createdAt),
            endAt: new Date(),
            mode: 'singleUnitShort',
          })
        : undefined,
    [formatDuration, createdAt],
  )

  // eslint-disable-next-line sonarjs/no-identical-functions
  const handleOpenProfile = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    openUnsafeUrlDialog([profileUrl, 'x'])
  }

  return (
    <OpenButton
      href={profileUrl}
      aria-label={t('common.open')}
      onClick={handleOpenProfile}
    >
      {username} {createdAtFormatted}
      <ExternalUrlIcon size={12} />
    </OpenButton>
  )
}

type HeadingExternalLinkProps = {
  url?: string
}

export const HeadingExternalLink = ({ url }: HeadingExternalLinkProps) => {
  const openUnsafeUrlDialog = useAction(unsafeUrlDialogEvent)

  const resourceUrl = useMemo(
    () => (url ? normalizeXHref(url) : undefined),
    [url],
  )

  const handleOpenResource = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (resourceUrl) {
      openUnsafeUrlDialog([resourceUrl, 'website'])
    }
  }

  if (!resourceUrl) {
    return undefined
  }

  return (
    <ExternalLinkRoot href={resourceUrl} onClick={handleOpenResource}>
      {resourceUrl}
    </ExternalLinkRoot>
  )
}

export const ExploreDevTwitterTooltipDefaultHeading = ({
  twitterInfo,
}: ExploreDevTwitterTooltipProps) => {
  return (
    <Heading>
      <HeadingTop data-has-avatar={Boolean(twitterInfo.profileImageUrl)}>
        {twitterInfo.profileImageUrl ? (
          <ImagePopover src={twitterInfo.profileImageUrl} alt="username">
            <Avatar
              src={twitterInfo.profileImageUrl}
              alt="username"
              size={42}
            />
          </ImagePopover>
        ) : undefined}
        <Titles>
          <HeadingName
            username={twitterInfo.username}
            name={twitterInfo.name}
            verified={twitterInfo.verified}
          />
          <HeadingUsername
            username={twitterInfo.username}
            createdAt={twitterInfo.createdAt}
          />
        </Titles>
      </HeadingTop>

      <HeadingExternalLink url={twitterInfo.url} />
    </Heading>
  )
}

const Titles = styled.div`
  display: grid;
  gap: 1px;
  min-width: 0;
  justify-items: start;
  text-align: left;
`

const NameRoot = styled.a`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;

  color: var(--text-12);

  font-size: 16px;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`

const HeadingTop = styled.div`
  display: grid;
  align-items: center;
  gap: 8px;

  &[data-has-avatar='true'] {
    grid-template-columns: auto 1fr;
  }

  &[data-has-avatar='false'] {
    grid-template-columns: 1fr;
  }
`
const Heading = styled.div`
  display: flex;
  flex-direction: column;

  padding: 8px 8px 0;
  gap: 4px;
`

const OpenButton = styled.a`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  gap: 4px;

  color: var(--text-9);

  font-size: 13px;
  font-weight: 400;
  transition: all 0.15s ease;
  svg {
    transition: all 0.15s ease;
  }

  &:hover {
    color: var(--text-12);
    svg {
      color: var(--text-12);
    }
  }
`

const ExternalLinkRoot = styled.a`
  color: var(--primary-9);
  cursor: pointer;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  background: var(--background-1);
  padding: 4px;
  border-radius: 4px;
  word-break: break-all;
  &:hover {
    text-decoration: underline;
  }
`
